import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';

/**
 * Shown when the author adds a custom question to a survey that uses question
 * groups: "Should this question also become part of a group?"
 *  - picking a group  -> the question is appended to that group in the bank
 *  - "No"             -> the question is created in the bank without a group
 */
export default function AddQuestionGroupPrompt({ open, usedGroupIds, onChoose, onClose }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const [groups, setGroups] = useState(null);
  const [choice, setChoice] = useState('none');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setChoice('none');
    setGroups(null);
    void (async () => {
      try {
        const data = await apiFetch('/admin/question-groups');
        if (cancelled) return;
        const used = new Set((usedGroupIds || []).map(Number));
        setGroups(data.filter((g) => used.has(Number(g.id))));
      } catch {
        if (!cancelled) setGroups([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, open, usedGroupIds]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add question to a group?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The new question is always saved to the question bank. You can also add it to one of the
          groups used by this survey so it becomes part of that template.
        </Typography>
        {groups === null ? (
          <CircularProgress size={20} />
        ) : (
          <FormControl>
            <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
              {groups.map((g) => (
                <FormControlLabel
                  key={g.id}
                  value={String(g.id)}
                  control={<Radio size="small" />}
                  label={g.name}
                />
              ))}
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label="No — question bank only"
              />
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onChoose(choice === 'none' ? null : Number(choice))}
          sx={{
            textTransform: 'none',
            backgroundColor: '#000000',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          Add question
        </Button>
      </DialogActions>
    </Dialog>
  );
}
